/**
 * WorkOrderFormBuilder - customize the member-facing work order / green sheet form.
 * Uses the same 3-tab layout as AR and Violation form builders.
 */
import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Switch, Button, Paper,
  Snackbar, Alert, Grid, Chip, Tabs, Tab,
} from "@mui/material";
import SaveIcon       from "@mui/icons-material/Save";
import PreviewIcon    from "@mui/icons-material/Preview";
import LabelIcon      from "@mui/icons-material/Label";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../utils/api";

const DEFAULT_CONFIG = {
  title:        "Create New Work Order",
  subtitle:     "Submit a maintenance request for this property.",
  submit_label: "Submit Work Order",
  fields: {
    section_title:   "Work Order Information",
    type:            true,
    subtype:         true,
    location:        true,
    description:     true,
    details:         true,
    attachments:     true,
    notify_checkbox: true,
    notify_label:    "Please contact me back regarding this request",
  },
  field_labels: {
    type:        "Work Order Type",
    subtype:     "Subtype",
    location:    "Location",
    description: "Description",
    details:     "Details / Notes",
    attachments: "Attach Files",
  },
};

const FIELD_GROUPS = [
  {
    step: "Form Fields",
    fields: [
      { key: "type",        required: true },
      { key: "subtype" },
      { key: "location" },
      { key: "description", required: true },
      { key: "details" },
      { key: "attachments" },
      { key: "notify_checkbox" },
    ],
  },
];

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const primaryBtnSx = {
  backgroundColor:"#2f63ad", color:"white", fontWeight:700,
  borderRadius:"10px", textTransform:"none", boxShadow:"none",
  "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" },
};

const PreviewField = ({ label, required }) => (
  <Box sx={{ mb:1.25, height:36, borderRadius:"8px", border:"1px solid #d0d7e2",
    bgcolor:"white", display:"flex", alignItems:"center", px:1.5 }}>
    <Typography variant="caption" sx={{ color:"#9ca3af" }}>
      {label}{required ? " *" : ""}
    </Typography>
  </Box>
);

const WorkOrderFormBuilder = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saving, setSaving]  = useState(false);
  const [snack,  setSnack]   = useState({ open:false, msg:"", sev:"success" });
  const [tab,    setTab]     = useState(0);

  useEffect(() => {
    api.get("/wo-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setConfig(prev => ({
          ...DEFAULT_CONFIG, ...r.data,
          fields:       { ...DEFAULT_CONFIG.fields,       ...(r.data.fields || {}) },
          field_labels: { ...DEFAULT_CONFIG.field_labels, ...(r.data.field_labels || {}) },
        }));
      }
    }).catch(() => {});
  }, []);

  const setTop        = (k,v) => setConfig(c => ({ ...c, [k]: v }));
  const setField      = (k,v) => setConfig(c => ({ ...c, fields:       { ...c.fields,       [k]: v } }));
  const setFieldLabel = (k,v) => setConfig(c => ({ ...c, field_labels: { ...c.field_labels, [k]: v } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/wo-form-config", config);
      setSnack({ open:true, msg:"Work order form settings saved.", sev:"success" });
    } catch {
      setSnack({ open:true, msg:"Failed to save.", sev:"error" });
    } finally { setSaving(false); }
  };

  const f  = config.fields       || {};
  const fl = config.field_labels || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>
            Member Form Builder
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Customize the work order / green sheet submission form that members see on the Owner Portal.
            Changes take effect immediately.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={primaryBtnSx}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v) => setTab(v)}
          sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:46 } }}>
          <Tab icon={<LabelIcon fontSize="small" />} iconPosition="start" label="Labels & Text" />
          <Tab icon={<VisibilityIcon fontSize="small" />} iconPosition="start" label="Field Visibility" />
          <Tab icon={<PreviewIcon fontSize="small" />} iconPosition="start" label="Preview" />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Labels & Text ─────────────────────────────────────────── */}
      {tab === 0 && (<>
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Form Header</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Form Title" value={config.title}
                onChange={e => setTop("title", e.target.value)} sx={fieldSx}
                helperText="Main heading shown at top of the form" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Submit Button Label" value={config.submit_label}
                onChange={e => setTop("submit_label", e.target.value)} sx={fieldSx}
                helperText='e.g. "Submit Green Sheet"' />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Form Subtitle" value={config.subtitle}
                onChange={e => setTop("subtitle", e.target.value)} sx={fieldSx}
                helperText="Description line below the title" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Section Heading" value={config.fields?.section_title || ""}
                onChange={e => setField("section_title", e.target.value)} sx={fieldSx}
                helperText='Heading inside the form (e.g. "Green Sheet Information")' />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Field Labels</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
            Rename any field to match your HOA's terminology.
          </Typography>
          <Grid container spacing={1.5}>
            {[
              { key:"type",        default:"Work Order Type" },
              { key:"subtype",     default:"Subtype" },
              { key:"location",    default:"Location" },
              { key:"description", default:"Description" },
              { key:"details",     default:"Details / Notes" },
              { key:"attachments", default:"Attach Files" },
            ].map(({ key, default: def }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField fullWidth size="small"
                  label={`"${def}" label`}
                  value={fl[key] || ""}
                  onChange={e => setFieldLabel(key, e.target.value)}
                  sx={fieldSx} />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Notification checkbox label */}
        {f.notify_checkbox && (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>
              Notification Checkbox Label
            </Typography>
            <TextField fullWidth
              label="Checkbox Label"
              value={f.notify_label || ""}
              onChange={e => setField("notify_label", e.target.value)}
              sx={fieldSx}
              helperText="Text shown next to the notification preference checkbox." />
            <Box sx={{ mt:2, p:1.5, borderRadius:"10px", bgcolor:"rgba(15,118,110,0.04)", border:"1px solid rgba(15,118,110,0.12)" }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#0f766e" }}>How it works:</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mt:0.25 }}>
                Checked - member receives all email notifications (creation, status changes, comments).
              </Typography>
              <Typography variant="caption" sx={{ color:"#6b778c", display:"block" }}>
                Unchecked - member receives no emails. Staff notifications are unaffected.
              </Typography>
            </Box>
          </Paper>
        )}
      </>)}

      {/* ── Tab 1: Field Visibility ───────────────────────────────────────── */}
      {tab === 1 && (
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
            Field Visibility
          </Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
            Toggle which fields appear on the member form. Required fields cannot be hidden.
          </Typography>
          <Grid container spacing={1}>
            {[
              { key:"type",            label:"Work Order Type dropdown", required:true },
              { key:"subtype",         label:"Subtype dropdown" },
              { key:"location",        label:"Location dropdown" },
              { key:"description",     label:"Description field",        required:true },
              { key:"details",         label:"Details / Notes field" },
              { key:"attachments",     label:"File attachment section" },
              { key:"notify_checkbox", label:"Notification preference checkbox" },
            ].map(({ key, label, required }) => (
              <Grid item xs={12} sm={6} key={key}>
                <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                  px:1.5, py:1, borderRadius:"10px",
                  bgcolor: f[key] ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)",
                  border:"1px solid rgba(47,99,173,0.08)" }}>
                  <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                    <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>
                      {fl[key] || label}
                    </Typography>
                    {required && (
                      <Chip label="Required" size="small"
                        sx={{ fontSize:10, height:18, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad" }} />
                    )}
                  </Box>
                  <Switch checked={Boolean(f[key])} disabled={required}
                    onChange={e => setField(key, e.target.checked)} size="small" />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Tab 2: Preview ───────────────────────────────────────────────── */}
      {tab === 2 && (
        <Paper elevation={0} sx={{ ...cardSx, bgcolor:"rgba(47,99,173,0.015)" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2 }}>
            <PreviewIcon sx={{ color:"#2f63ad", fontSize:18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Form Preview</Typography>
          </Box>

          <Box sx={{ border:"1px solid rgba(47,99,173,0.15)", borderRadius:"12px", p:2.5, bgcolor:"white" }}>
            <Typography variant="h6" sx={{ fontWeight:700, color:"#223142", mb:0.25 }}>
              {config.title || "Create New Work Order"}
            </Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
              {config.subtitle}
            </Typography>

            <Box sx={{ bgcolor:"rgba(47,99,173,0.04)", borderRadius:"10px", p:1.5, mb:2 }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#2f63ad" }}>
                {f.section_title || "Work Order Information"}
              </Typography>
            </Box>

            <Box>
              {f.type        && <PreviewField label={fl.type        || "Work Order Type"} required />}
              {f.subtype     && <PreviewField label={fl.subtype     || "Subtype"} />}
              {f.location    && <PreviewField label={fl.location    || "Location"} />}
              {f.description && <PreviewField label={fl.description || "Description"} required />}
              {f.details     && <PreviewField label={fl.details     || "Details / Notes"} />}
              {f.attachments && (
                <Box sx={{ mb:1.25, p:1.5, borderRadius:"8px", border:"2px dashed #90caf9", textAlign:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>
                    {fl.attachments || "Attach Files"} (upload area)
                  </Typography>
                </Box>
              )}
              {f.notify_checkbox && (
                <Box sx={{ display:"flex", alignItems:"center", gap:1, mt:1, p:1, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.08)" }}>
                  <Box sx={{ width:16, height:16, border:"2px solid #2f63ad", borderRadius:3 }} />
                  <Typography variant="caption" sx={{ color:"#223142" }}>
                    {f.notify_label || "Please contact me back regarding this request"}
                  </Typography>
                </Box>
              )}
            </Box>

            <Button variant="contained" size="small" disabled
              sx={{ mt:2, borderRadius:"8px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad" }}>
              {config.submit_label || "Submit Work Order"}
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(s => ({...s,open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({...s,open:false}))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderFormBuilder;
